import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Container,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Checkbox,
    FormControlLabel,
    Autocomplete,
    TextField,
    SelectChangeEvent,
    Alert,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    RadioGroup,
    Radio,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { getObras, Obra } from '../services/api';
import { supabase } from '../services/supabaseClient';
import { AnexoMode, countReportAttachments } from '../services/pdfGenerator';
import { useReportJobs } from '../contexts/ReportJobsContext';

type ReportType =
    | 'resumo-por-categoria'
    | 'detalhado-por-categoria'
    | 'detalhado-por-fase'
    | 'quantitativo-por-fase'
    | 'detalhado-por-lancamento';

const REPORT_OPTIONS: { value: ReportType; label: string; rpc: string; description: string }[] = [
    {
        value: 'resumo-por-categoria',
        label: 'Resumo de Custos por Categoria',
        rpc: 'get_relatorio_resumo_por_categoria',
        description: 'Visão consolidada de custos agrupados por categoria principal e subcategoria.',
    },
    {
        value: 'detalhado-por-categoria',
        label: 'Relatório Detalhado por Categoria',
        rpc: 'get_relatorio_detalhado_por_categoria',
        description: 'Lista todos os itens adquiridos dentro de cada categoria e subcategoria.',
    },
    {
        value: 'detalhado-por-fase',
        label: 'Relatório Detalhado por Fase da Obra',
        rpc: 'get_relatorio_detalhado_por_fase',
        description: 'Detalhamento cronológico dos itens e fornecedores em cada etapa da obra.',
    },
    {
        value: 'quantitativo-por-fase',
        label: 'Relatório Quantitativo por Fase (Itens e Preço Médio)',
        rpc: 'get_relatorio_quantitativo_por_fase',
        description: 'Soma as quantidades totais e calcula o preço médio de cada insumo por fase.',
    },
    {
        value: 'detalhado-por-lancamento',
        label: 'Relatório Detalhado por Lançamento (Notas e Comprovantes)',
        rpc: 'get_relatorio_detalhado_por_lancamento',
        description: 'Agrupado por data e nota fiscal, com opção de anexar todos os comprovantes no PDF.',
    },
];

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(value) || 0);

const formatDateUtc = (dateStr?: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const toIsoDate = (date: Date) => date.toISOString().split('T')[0];

const RelatoriosPage: React.FC = () => {
    const [obras, setObras] = useState<Obra[]>([]);
    const [selectedObra, setSelectedObra] = useState<Obra | null>(null);
    const [reportType, setReportType] = useState<ReportType>('resumo-por-categoria');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [includeAnexos, setIncludeAnexos] = useState(false);
    const [anexoMode, setAnexoMode] = useState<AnexoMode>('optimized');

    const [previewData, setPreviewData] = useState<any[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { startReportJob, jobs } = useReportJobs();

    useEffect(() => {
        getObras().then((data) => {
            const list = data || [];
            setObras(list);
            if (list.length > 0) {
                setSelectedObra(list[0]);
            }
        });
    }, []);

    const dateRangeError = useMemo(() => {
        if (dataInicio && dataFim && dataFim < dataInicio) {
            return 'A Data Fim não pode ser anterior à Data Início.';
        }
        return null;
    }, [dataInicio, dataFim]);

    const applyQuickPeriod = (preset: 'all' | 'this_month' | 'last_month' | 'last_90_days' | 'this_year') => {
        const now = new Date();
        if (preset === 'all') {
            setDataInicio('');
            setDataFim('');
            return;
        }
        if (preset === 'this_month') {
            const first = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
            const last = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0));
            setDataInicio(toIsoDate(first));
            setDataFim(toIsoDate(last));
            return;
        }
        if (preset === 'last_month') {
            const first = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
            const last = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 0));
            setDataInicio(toIsoDate(first));
            setDataFim(toIsoDate(last));
            return;
        }
        if (preset === 'last_90_days') {
            const start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            setDataInicio(toIsoDate(start));
            setDataFim(toIsoDate(now));
            return;
        }
        if (preset === 'this_year') {
            const first = new Date(Date.UTC(now.getFullYear(), 0, 1));
            const last = new Date(Date.UTC(now.getFullYear(), 11, 31));
            setDataInicio(toIsoDate(first));
            setDataFim(toIsoDate(last));
        }
    };

    const fetchReportData = useCallback(async () => {
        if (!selectedObra || dateRangeError) {
            setPreviewData([]);
            return;
        }

        const option = REPORT_OPTIONS.find((o) => o.value === reportType);
        if (!option) return;

        setPreviewLoading(true);
        setError(null);
        try {
            const rpcParams = {
                p_obra_id: selectedObra.id,
                p_data_inicio: dataInicio || undefined,
                p_data_fim: dataFim || undefined,
            };

            const { data, error: rpcError } = await supabase.rpc(option.rpc, rpcParams);
            if (rpcError) throw rpcError;
            setPreviewData(Array.isArray(data) ? data : []);
        } catch (err: any) {
            console.error('Erro ao carregar dados do relatório:', err);
            setError(err.message || 'Erro ao buscar dados do relatório.');
            setPreviewData([]);
        } finally {
            setPreviewLoading(false);
        }
    }, [selectedObra, reportType, dataInicio, dataFim, dateRangeError]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const totalPeriodo = useMemo(() => {
        return (previewData || []).reduce(
            (acc, g) => acc + (Number(g.total ?? g.total_grupo ?? g.subtotal_fase ?? 0)),
            0
        );
    }, [previewData]);

    const totalAttachmentsCount = useMemo(() => {
        if (reportType !== 'detalhado-por-lancamento') return 0;
        return countReportAttachments(previewData);
    }, [reportType, previewData]);

    const isCurrentReportProcessing = useMemo(() => {
        if (!selectedObra) return false;
        return jobs.some(
            (j) =>
                j.status === 'processing' &&
                j.obraId === selectedObra.id &&
                j.reportType === reportType
        );
    }, [jobs, selectedObra, reportType]);

    const handleGeneratePDF = () => {
        if (!selectedObra) return;
        setError(null);
        const option = REPORT_OPTIONS.find((o) => o.value === reportType);

        startReportJob({
            obra: selectedObra,
            reportData: previewData,
            reportType,
            reportLabel: option?.label || 'Relatório da Obra',
            includeAnexos,
            anexoMode,
            periodo: { dataInicio: dataInicio || undefined, dataFim: dataFim || undefined },
        });
    };

    const handleExportCSV = () => {
        if (!selectedObra || previewData.length === 0) return;

        const escapeCsv = (val: any) => {
            const str = String(val ?? '').replace(/"/g, '""');
            return `"${str}"`;
        };
        const fmtDec = (val: any) => (Number(val) || 0).toFixed(2).replace('.', ',');

        const rows: string[][] = [];

        if (reportType === 'resumo-por-categoria') {
            rows.push(['Categoria Principal', 'Subcategoria', 'Valor Total (R$)']);
            previewData.forEach((cat: any) => {
                rows.push([cat.nome, '(Total da Categoria)', fmtDec(cat.total)]);
                (cat.subcategorias || []).forEach((sub: any) => {
                    rows.push([cat.nome, sub.nome, fmtDec(sub.total)]);
                });
            });
        } else if (reportType === 'detalhado-por-categoria') {
            rows.push(['Categoria Principal', 'Subcategoria', 'Descrição do Item', 'Quantidade', 'Valor Unitário (R$)', 'Valor Líquido (R$)']);
            previewData.forEach((cat: any) => {
                (cat.items || []).forEach((it: any) => {
                    rows.push([cat.nome, '-', it.descricao, fmtDec(it.quantidade), fmtDec(it.valor_unitario), fmtDec(it.valor_liquido)]);
                });
                (cat.subcategorias || []).forEach((sub: any) => {
                    (sub.items || []).forEach((it: any) => {
                        rows.push([cat.nome, sub.nome, it.descricao, fmtDec(it.quantidade), fmtDec(it.valor_unitario), fmtDec(it.valor_liquido)]);
                    });
                });
            });
        } else if (reportType === 'detalhado-por-fase') {
            rows.push(['Fase da Obra', 'Data', 'Descrição do Item', 'Fornecedor', 'Categoria', 'Quantidade', 'Valor Unitário (R$)', 'Valor Líquido (R$)']);
            previewData.forEach((fase: any) => {
                (fase.items || []).forEach((it: any) => {
                    rows.push([
                        fase.nome,
                        formatDateUtc(it.data_lancamento),
                        it.descricao,
                        it.fornecedor_nome || '-',
                        it.categoria_nome || '-',
                        fmtDec(it.quantidade),
                        fmtDec(it.valor_unitario),
                        fmtDec(it.valor_liquido),
                    ]);
                });
            });
        } else if (reportType === 'quantitativo-por-fase') {
            rows.push(['Fase da Obra', 'Descrição do Item', 'Quantidade Total', 'Preço Médio (R$)', 'Valor Total (R$)']);
            previewData.forEach((fase: any) => {
                const nomeFase = fase.fase_nome || fase.nome || 'Sem Fase Definida';
                (fase.itens || []).forEach((it: any) => {
                    rows.push([
                        nomeFase,
                        it.descricao,
                        fmtDec(it.quantidade_total),
                        fmtDec(it.preco_medio),
                        fmtDec(it.valor_total),
                    ]);
                });
            });
        } else if (reportType === 'detalhado-por-lancamento') {
            rows.push(['Data', 'Nº Lançamento', 'Fornecedor', 'Nota Fiscal', 'Descrição do Item', 'Quantidade', 'Valor do Item (R$)', 'Total da Nota (R$)']);
            previewData.forEach((grupo: any) => {
                (grupo.items || []).forEach((lanc: any) => {
                    (lanc.items || []).forEach((it: any) => {
                        rows.push([
                            grupo.group_name,
                            lanc.lancamento_id,
                            lanc.fornecedor_nome || 'N/A',
                            lanc.numero_nota || 'N/A',
                            it.descricao,
                            fmtDec(it.quantidade),
                            fmtDec(it.valor_total),
                            fmtDec(lanc.valor_total_lancamento),
                        ]);
                    });
                });
            });
        }

        const csvContent = '\uFEFF' + rows.map((r) => r.map(escapeCsv).join(';')).join('\r\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `relatorio_${reportType}_${selectedObra.nome.toLowerCase().replace(/\s+/g, '_')}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const currentReportInfo = REPORT_OPTIONS.find((o) => o.value === reportType);
    const orcamentoObra = Number(selectedObra?.orcamento_inicial) || 0;
    const pctOrcamento = orcamentoObra > 0 ? (totalPeriodo / orcamentoObra) * 100 : 0;

    return (
        <Container maxWidth="lg" sx={{ my: 4 }}>
            {/* Cabeçalho */}
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                    Central de Relatórios
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Analise os custos da obra na tela e exporte relatórios detalhados em PDF ou Planilha (CSV/Excel)
                </Typography>
            </Box>

            {/* Painel de Filtros e Ações */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5, mb: 2.5 }}>
                    <Autocomplete
                        options={obras}
                        getOptionLabel={(option) =>
                            option.municipio ? `${option.nome} (${option.municipio})` : option.nome
                        }
                        value={selectedObra}
                        onChange={(_event, newValue) => setSelectedObra(newValue)}
                        renderInput={(params) => <TextField {...params} label="Selecione a Obra" size="small" />}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel id="report-type-label">Tipo de Relatório</InputLabel>
                        <Select
                            labelId="report-type-label"
                            value={reportType}
                            onChange={(e: SelectChangeEvent) => setReportType(e.target.value as ReportType)}
                            label="Tipo de Relatório"
                        >
                            {REPORT_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {currentReportInfo && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {currentReportInfo.description}
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Filtro de Período e Atalhos */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexGrow: 1 }}>
                        <TextField
                            size="small"
                            label="Data Início"
                            type="date"
                            value={dataInicio}
                            onChange={(e) => setDataInicio(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: '9999-12-31' }}
                            sx={{ minWidth: 170 }}
                        />
                        <TextField
                            size="small"
                            label="Data Fim"
                            type="date"
                            value={dataFim}
                            onChange={(e) => setDataFim(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: '9999-12-31' }}
                            sx={{ minWidth: 170 }}
                        />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <CalendarMonthOutlinedIcon fontSize="small" color="action" />
                        <Chip
                            label="Todo o Período"
                            size="small"
                            variant={!dataInicio && !dataFim ? 'filled' : 'outlined'}
                            color={!dataInicio && !dataFim ? 'primary' : 'default'}
                            onClick={() => applyQuickPeriod('all')}
                        />
                        <Chip
                            label="Este Mês"
                            size="small"
                            variant="outlined"
                            onClick={() => applyQuickPeriod('this_month')}
                        />
                        <Chip
                            label="Mês Passado"
                            size="small"
                            variant="outlined"
                            onClick={() => applyQuickPeriod('last_month')}
                        />
                        <Chip
                            label="Últimos 90 Dias"
                            size="small"
                            variant="outlined"
                            onClick={() => applyQuickPeriod('last_90_days')}
                        />
                        <Chip
                            label="Este Ano"
                            size="small"
                            variant="outlined"
                            onClick={() => applyQuickPeriod('this_year')}
                        />
                    </Box>
                </Box>

                {reportType === 'detalhado-por-lancamento' && (
                    <Box
                        sx={{
                            mt: 2.5,
                            p: 2,
                            borderRadius: 1.5,
                            bgcolor: 'action.hover',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeAnexos}
                                        onChange={(e) => setIncludeAnexos(e.target.checked)}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Incluir comprovantes e anexos no final do PDF
                                    </Typography>
                                }
                            />
                            <Chip
                                icon={<AttachFileIcon fontSize="small" />}
                                label={`${totalAttachmentsCount} ${totalAttachmentsCount === 1 ? 'comprovante encontrado' : 'comprovantes encontrados'} no período`}
                                size="small"
                                color={totalAttachmentsCount > 0 ? 'primary' : 'default'}
                                variant="outlined"
                            />
                        </Box>

                        {includeAnexos && (
                            <Box sx={{ mt: 1.5, pl: { xs: 0, sm: 4 } }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                    Modo de inclusão dos comprovantes:
                                </Typography>
                                <RadioGroup
                                    value={anexoMode}
                                    onChange={(e) => setAnexoMode(e.target.value as AnexoMode)}
                                >
                                    <FormControlLabel
                                        value="optimized"
                                        control={<Radio size="small" />}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Embutir Imagens Otimizadas (Recomendado — Rápido e PDF Leve)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Download paralelo em lotes e compressão inteligente para folha A4 (até 5x mais rápido, ideal para WhatsApp e E-mail).
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start', mb: 1 }}
                                    />
                                    <FormControlLabel
                                        value="high_res"
                                        control={<Radio size="small" />}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Embutir em Alta Definição
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Mantém resolução superior para impressão detalhada (gera arquivos maiores).
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start', mb: 1 }}
                                    />
                                    <FormControlLabel
                                        value="links_only"
                                        control={<Radio size="small" />}
                                        label={
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    Apenas Índice com Links Clicáveis (Geração Instantânea &lt; 1s)
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Não baixa imagens pesadas; adiciona uma tabela no final do PDF com botão clicável para abrir cada comprovante no navegador.
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: 'flex-start' }}
                                    />
                                </RadioGroup>
                            </Box>
                        )}
                    </Box>
                )}

                {dateRangeError && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {dateRangeError}
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {isCurrentReportProcessing && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Este relatório já está sendo preparado em segundo plano (acompanhe no painel no canto inferior direito).{' '}
                        <strong>Você pode navegar livremente e utilizar qualquer outra tela do sistema</strong> — o download começará automaticamente assim que terminar.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleGeneratePDF}
                        disabled={isCurrentReportProcessing || previewLoading || !selectedObra || !!dateRangeError || previewData.length === 0}
                        sx={{ fontWeight: 600, px: 3 }}
                    >
                        {isCurrentReportProcessing ? 'Gerando em 2º Plano...' : 'Baixar Relatório em PDF'}
                    </Button>

                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<TableViewIcon />}
                        onClick={handleExportCSV}
                        disabled={previewLoading || !selectedObra || !!dateRangeError || previewData.length === 0}
                        sx={{ fontWeight: 600, px: 3 }}
                    >
                        Exportar Planilha (CSV/Excel)
                    </Button>
                </Box>
            </Paper>

            {/* Resumo Executivo e Pré-visualização */}
            {selectedObra && !dateRangeError && (
                <>
                    {/* Cards de Resumo */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                                Custo Total no Período
                            </Typography>
                            <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                                {formatCurrency(totalPeriodo)}
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                                Orçamento Inicial da Obra
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {orcamentoObra > 0 ? formatCurrency(orcamentoObra) : 'Não definido'}
                            </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                                Representatividade no Orçamento
                            </Typography>
                            <Typography
                                variant="h5"
                                sx={{
                                    fontWeight: 700,
                                    color: pctOrcamento > 100 ? 'error.main' : 'success.main',
                                }}
                            >
                                {orcamentoObra > 0 ? `${pctOrcamento.toFixed(1).replace('.', ',')}%` : '-'}
                            </Typography>
                        </Paper>
                    </Box>

                    {/* Tabela de Pré-visualização */}
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Pré-visualização: {currentReportInfo?.label}
                            </Typography>
                            <Chip
                                label={`${previewData.length} ${previewData.length === 1 ? 'grupo' : 'grupos'}`}
                                size="small"
                                variant="outlined"
                            />
                        </Box>

                        {previewLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                <CircularProgress />
                            </Box>
                        ) : previewData.length === 0 ? (
                            <Alert severity="info">
                                Nenhum lançamento encontrado para a obra e período selecionados.
                            </Alert>
                        ) : reportType === 'resumo-por-categoria' ? (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Categoria / Subcategoria</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                % do Período
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                Valor Total
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.map((cat: any) => {
                                            const pctCat =
                                                totalPeriodo > 0 ? ((Number(cat.total) / totalPeriodo) * 100).toFixed(1) : '0.0';
                                            return (
                                                <React.Fragment key={cat.id}>
                                                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{cat.nome}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                            {pctCat.replace('.', ',')}%
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                            {formatCurrency(cat.total)}
                                                        </TableCell>
                                                    </TableRow>
                                                    {(cat.subcategorias || []).map((sub: any) => {
                                                        const pctSub =
                                                            totalPeriodo > 0
                                                                ? ((Number(sub.total) / totalPeriodo) * 100).toFixed(1)
                                                                : '0.0';
                                                        return (
                                                            <TableRow key={sub.id} hover>
                                                                <TableCell sx={{ pl: 4 }}>• {sub.nome}</TableCell>
                                                                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                                                    {pctSub.replace('.', ',')}%
                                                                </TableCell>
                                                                <TableCell align="right">{formatCurrency(sub.total)}</TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : reportType === 'detalhado-por-categoria' ? (
                            <Box>
                                {previewData.map((cat: any) => (
                                    <Accordion key={cat.id} defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                                                <Typography sx={{ fontWeight: 700 }}>{cat.nome}</Typography>
                                                <Typography color="primary" sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(cat.total)}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {(cat.subcategorias || []).map((sub: any) => (
                                                <Box key={sub.id} sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                            {sub.nome}
                                                        </Typography>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            {formatCurrency(sub.total)}
                                                        </Typography>
                                                    </Box>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Descrição</TableCell>
                                                                <TableCell align="right">Qtd</TableCell>
                                                                <TableCell align="right">Vl. Unitário</TableCell>
                                                                <TableCell align="right">Vl. Líquido</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {(sub.items || []).map((it: any, idx: number) => (
                                                                <TableRow key={idx} hover>
                                                                    <TableCell>{it.descricao}</TableCell>
                                                                    <TableCell align="right">{formatNumber(it.quantidade)}</TableCell>
                                                                    <TableCell align="right">{formatCurrency(it.valor_unitario)}</TableCell>
                                                                    <TableCell align="right">{formatCurrency(it.valor_liquido)}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </Box>
                                            ))}
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        ) : reportType === 'detalhado-por-fase' ? (
                            <Box>
                                {previewData.map((fase: any, idx: number) => (
                                    <Accordion key={idx} defaultExpanded={idx === 0}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                                                <Typography sx={{ fontWeight: 700 }}>{fase.nome}</Typography>
                                                <Typography color="primary" sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(fase.total)}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Data</TableCell>
                                                        <TableCell>Descrição</TableCell>
                                                        <TableCell>Fornecedor</TableCell>
                                                        <TableCell align="right">Qtd</TableCell>
                                                        <TableCell align="right">Vl. Unit.</TableCell>
                                                        <TableCell align="right">Vl. Líquido</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(fase.items || []).map((it: any, i: number) => (
                                                        <TableRow key={i} hover>
                                                            <TableCell>{formatDateUtc(it.data_lancamento)}</TableCell>
                                                            <TableCell>{it.descricao}</TableCell>
                                                            <TableCell>{it.fornecedor_nome || '-'}</TableCell>
                                                            <TableCell align="right">{formatNumber(it.quantidade)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(it.valor_unitario)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(it.valor_liquido)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        ) : reportType === 'quantitativo-por-fase' ? (
                            <Box>
                                {previewData.map((fase: any, idx: number) => (
                                    <Accordion key={idx} defaultExpanded={idx === 0}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                    {fase.fase_nome || fase.nome}
                                                </Typography>
                                                <Typography color="primary" sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(fase.subtotal_fase ?? fase.total)}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Descrição do Item</TableCell>
                                                        <TableCell align="right">Quantidade Total</TableCell>
                                                        <TableCell align="right">Preço Médio</TableCell>
                                                        <TableCell align="right">Valor Total</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(fase.itens || []).map((it: any, i: number) => (
                                                        <TableRow key={i} hover>
                                                            <TableCell>{it.descricao}</TableCell>
                                                            <TableCell align="right">{formatNumber(it.quantidade_total)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(it.preco_medio)}</TableCell>
                                                            <TableCell align="right">{formatCurrency(it.valor_total)}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        ) : (
                            <Box>
                                {previewData.map((grupo: any, idx: number) => (
                                    <Accordion key={idx} defaultExpanded={idx === 0}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                                                <Typography sx={{ fontWeight: 700 }}>Data: {grupo.group_name}</Typography>
                                                <Typography color="primary" sx={{ fontWeight: 700 }}>
                                                    {formatCurrency(grupo.total_grupo)}
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Fornecedor</TableCell>
                                                        <TableCell>Nota Fiscal</TableCell>
                                                        <TableCell>Itens</TableCell>
                                                        <TableCell align="right">Valor do Lançamento</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(grupo.items || []).map((lanc: any) => (
                                                        <TableRow key={lanc.lancamento_id} hover>
                                                            <TableCell sx={{ fontWeight: 500 }}>{lanc.fornecedor_nome}</TableCell>
                                                            <TableCell>{lanc.numero_nota || 'N/A'}</TableCell>
                                                            <TableCell>
                                                                {(lanc.items || [])
                                                                    .map((i: any) => `${i.descricao} (${formatNumber(i.quantidade)})`)
                                                                    .join(', ')}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                                {formatCurrency(lanc.valor_total_lancamento)}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        )}
                    </Paper>
                </>
            )}
        </Container>
    );
};

export default RelatoriosPage;
